import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_2ff1966a from 'nuxt_plugin_plugin_2ff1966a' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_axios_0911f861 from 'nuxt_plugin_axios_0911f861' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_datedisplay_b60e0fce from 'nuxt_plugin_datedisplay_b60e0fce' // Source: ../plugins/date-display (mode: 'all')
import nuxt_plugin_datetimeinput_2d26fe13 from 'nuxt_plugin_datetimeinput_2d26fe13' // Source: ../plugins/date-time-input (mode: 'all')
import nuxt_plugin_plugins_e476404e from 'nuxt_plugin_plugins_e476404e' // Source: ../plugins (mode: 'client')
import nuxt_plugin_popover_173bbc46 from 'nuxt_plugin_popover_173bbc46' // Source: ../plugins/pop-over (mode: 'all')
import nuxt_plugin_vuejsonexcel_bac525a2 from 'nuxt_plugin_vuejsonexcel_bac525a2' // Source: ../plugins/vue-json-excel (mode: 'all')
import nuxt_plugin_axiosheadersfix_4caa4c7e from 'nuxt_plugin_axiosheadersfix_4caa4c7e' // Source: ../plugins/axios-headers-fix (mode: 'client')
import nuxt_plugin_axiosauth_274fe6d6 from 'nuxt_plugin_axiosauth_274fe6d6' // Source: ../plugins/axios-auth (mode: 'client')
import nuxt_plugin_upgrademodal_21dd4ff4 from 'nuxt_plugin_upgrademodal_21dd4ff4' // Source: ../plugins/upgrade-modal (mode: 'client')
import nuxt_plugin_userprofileinit_69d418c0 from 'nuxt_plugin_userprofileinit_69d418c0' // Source: ../plugins/user-profile-init (mode: 'client')
import nuxt_plugin_auth_9d1ebea2 from 'nuxt_plugin_auth_9d1ebea2' // Source: ./auth.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"titleTemplate":"EEZY Work","title":"EEZY Work","htmlAttrs":{"lang":"nl"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":""},{"name":"format-detection","content":"telephone=no"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"https:\u002F\u002Fmaps.googleapis.com\u002Fmaps\u002Fapi\u002Fjs?key=AIzaSyDpaoViOg3jb8ZYx3baSurmmaBoKUpekLU&libraries=places"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_2ff1966a === 'function') {
    await nuxt_plugin_plugin_2ff1966a(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_0911f861 === 'function') {
    await nuxt_plugin_axios_0911f861(app.context, inject)
  }

  if (typeof nuxt_plugin_datedisplay_b60e0fce === 'function') {
    await nuxt_plugin_datedisplay_b60e0fce(app.context, inject)
  }

  if (typeof nuxt_plugin_datetimeinput_2d26fe13 === 'function') {
    await nuxt_plugin_datetimeinput_2d26fe13(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_plugins_e476404e === 'function') {
    await nuxt_plugin_plugins_e476404e(app.context, inject)
  }

  if (typeof nuxt_plugin_popover_173bbc46 === 'function') {
    await nuxt_plugin_popover_173bbc46(app.context, inject)
  }

  if (typeof nuxt_plugin_vuejsonexcel_bac525a2 === 'function') {
    await nuxt_plugin_vuejsonexcel_bac525a2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_axiosheadersfix_4caa4c7e === 'function') {
    await nuxt_plugin_axiosheadersfix_4caa4c7e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_axiosauth_274fe6d6 === 'function') {
    await nuxt_plugin_axiosauth_274fe6d6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_upgrademodal_21dd4ff4 === 'function') {
    await nuxt_plugin_upgrademodal_21dd4ff4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_userprofileinit_69d418c0 === 'function') {
    await nuxt_plugin_userprofileinit_69d418c0(app.context, inject)
  }

  if (typeof nuxt_plugin_auth_9d1ebea2 === 'function') {
    await nuxt_plugin_auth_9d1ebea2(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
